import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Truck, Package, MapPin, Clock, CheckCircle, AlertTriangle, 
  Plus, Eye, Play, FileText, DollarSign, Building2, User,
  ArrowRight, Route, Calendar, MoreVertical, Loader2
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../lib/api';

const jobStatusConfig = {
  draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
  scheduled: { color: 'bg-blue-100 text-blue-800', label: 'Scheduled' },
  in_progress: { color: 'bg-amber-100 text-amber-800', label: 'In Progress' },
  completed: { color: 'bg-green-100 text-green-800', label: 'Completed' },
  cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
};

const tripStatusConfig = {
  pending: { color: 'bg-gray-100 text-gray-800', label: 'Pending' },
  in_transit: { color: 'bg-blue-100 text-blue-800', label: 'In Transit' },
  delivered: { color: 'bg-green-100 text-green-800', label: 'Delivered' },
  cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
};

export const TransportationPage = () => {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [trips, setTrips] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [clientPOs, setClientPOs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState('all');
  
  // Dialogs
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [tripDialogOpen, setTripDialogOpen] = useState(false);
  const [viewJobOpen, setViewJobOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Forms
  const [jobForm, setJobForm] = useState({
    company_id: '',
    client_po_id: '',
    job_type: 'single_trip',
    cargo_type: 'general',
    cargo_description: '',
    origin: '',
    destination: '',
    distance_km: '',
    rate_type: 'per_trip',
    rate_amount: '',
    vehicle_type: 'truck',
    is_outsourced: false,
    supplier_id: '',
    supplier_cost: '',
    scheduled_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [tripForm, setTripForm] = useState({
    job_id: '',
    cargo_description: '',
    cargo_weight: '',
    vehicle_number: '',
    driver_name: '',
    driver_phone: '',
    departure_date: new Date().toISOString().split('T')[0],
    departure_time: '',
    expected_arrival: '',
    trip_rate: '',
    supplier_cost: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedCompany]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, jobsRes, tripsRes, companiesRes, posRes, suppliersRes] = await Promise.all([
        api.get('/transportation/stats/summary', { params: selectedCompany !== 'all' ? { company_id: selectedCompany } : {} }),
        api.get('/transportation/jobs', { params: selectedCompany !== 'all' ? { company_id: selectedCompany } : {} }),
        api.get('/transportation/trips', { params: selectedCompany !== 'all' ? { company_id: selectedCompany } : {} }),
        api.get('/companies'),
        api.get('/client-po/list'),
        api.get('/suppliers')
      ]);
      
      setStats(statsRes.data);
      setJobs(jobsRes.data);
      setTrips(tripsRes.data);
      setCompanies(companiesRes.data);
      // Filter to transportation POs only
      setClientPOs(posRes.data.filter(po => po.po_type === 'transportation'));
      setSuppliers(suppliersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load transportation data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...jobForm,
        distance_km: jobForm.distance_km ? parseFloat(jobForm.distance_km) : null,
        rate_amount: parseFloat(jobForm.rate_amount) || 0,
        supplier_cost: jobForm.supplier_cost ? parseFloat(jobForm.supplier_cost) : null
      };
      
      await api.post('/transportation/jobs', payload);
      toast.success('Transport Job created');
      setJobDialogOpen(false);
      resetJobForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    }
  };

  const handleCreateTrip = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...tripForm,
        cargo_weight: tripForm.cargo_weight ? parseFloat(tripForm.cargo_weight) : null,
        trip_rate: tripForm.trip_rate ? parseFloat(tripForm.trip_rate) : null,
        supplier_cost: tripForm.supplier_cost ? parseFloat(tripForm.supplier_cost) : null
      };
      
      await api.post('/transportation/trips', payload);
      toast.success('Trip created');
      setTripDialogOpen(false);
      resetTripForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create trip');
    }
  };

  const handleStartJob = async (jobId) => {
    try {
      await api.patch(`/transportation/jobs/${jobId}/start`);
      toast.success('Job scheduled');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start job');
    }
  };

  const handleStartTrip = async (tripId) => {
    try {
      await api.patch(`/transportation/trips/${tripId}/start`);
      toast.success('Trip started');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trip');
    }
  };

  const handleDeliverTrip = async (tripId) => {
    try {
      const res = await api.patch(`/transportation/trips/${tripId}/deliver`, {});
      toast.success(`Trip delivered! DN: ${res.data.delivery_note_number}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deliver trip');
    }
  };

  const handleCreateInvoice = async (tripId) => {
    try {
      const res = await api.post(`/transportation/trips/${tripId}/create-invoice`);
      toast.success(`Invoice created: ${res.data.invoice_number}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleSupplierPayment = async (tripId) => {
    try {
      const res = await api.post(`/transportation/trips/${tripId}/supplier-payment`, {});
      toast.success(`Supplier payment recorded: ${res.data.amount}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const openAddTrip = (job) => {
    setTripForm({ ...tripForm, job_id: job.id });
    setSelectedJob(job);
    setTripDialogOpen(true);
  };

  const viewJobDetails = async (job) => {
    try {
      const res = await api.get(`/transportation/jobs/${job.id}`);
      setSelectedJob(res.data);
      setViewJobOpen(true);
    } catch (error) {
      toast.error('Failed to load job details');
    }
  };

  const resetJobForm = () => {
    setJobForm({
      company_id: '',
      client_po_id: '',
      job_type: 'single_trip',
      cargo_type: 'general',
      cargo_description: '',
      origin: '',
      destination: '',
      distance_km: '',
      rate_type: 'per_trip',
      rate_amount: '',
      vehicle_type: 'truck',
      is_outsourced: false,
      supplier_id: '',
      supplier_cost: '',
      scheduled_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
  };

  const resetTripForm = () => {
    setTripForm({
      job_id: '',
      cargo_description: '',
      cargo_weight: '',
      vehicle_number: '',
      driver_name: '',
      driver_phone: '',
      departure_date: new Date().toISOString().split('T')[0],
      departure_time: '',
      expected_arrival: '',
      trip_rate: '',
      supplier_cost: '',
      notes: ''
    });
  };

  // Filter suppliers by selected company
  const filteredSuppliers = suppliers.filter(s => 
    !jobForm.company_id || s.company_id === jobForm.company_id
  );

  // Filter Client POs by selected company
  const filteredPOs = clientPOs.filter(po => 
    !jobForm.company_id || po.company_id === jobForm.company_id
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transportation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Transportation Module</h1>
          <p className="text-muted-foreground">Manage transport jobs, trips, and invoicing</p>
        </div>
        <div className="flex gap-3">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-48" data-testid="company-filter">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setJobDialogOpen(true)} data-testid="create-job-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Transport Job
          </Button>
        </div>
      </div>

      {/* Workflow Diagram */}
      <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
        <h3 className="font-semibold mb-3">Transportation Workflow</h3>
        <div className="flex items-center justify-between gap-2 text-sm">
          {[
            { icon: FileText, label: 'Client PO', desc: 'Transportation PO' },
            { icon: Truck, label: 'Create Job', desc: 'Route & pricing' },
            { icon: Route, label: 'Add Trips', desc: 'Individual trips' },
            { icon: Package, label: 'Deliver', desc: 'Auto-creates DN' },
            { icon: DollarSign, label: 'Invoice', desc: 'Bill client' },
            { icon: CheckCircle, label: 'Pay Supplier', desc: 'If outsourced' }
          ].map((step, i) => (
            <React.Fragment key={i}>
              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mb-1">
                  <step.icon className="h-5 w-5 text-blue-700" />
                </div>
                <span className="font-medium">{step.label}</span>
                <span className="text-xs text-muted-foreground">{step.desc}</span>
              </div>
              {i < 5 && <ArrowRight className="h-4 w-4 text-blue-400 flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </Card>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Active Jobs</div>
            <div className="text-2xl font-bold text-blue-600">{stats.jobs.active}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">In Transit</div>
            <div className="text-2xl font-bold text-amber-600">{stats.trips.in_transit}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Delivered</div>
            <div className="text-2xl font-bold text-green-600">{stats.trips.delivered}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Pending Invoice</div>
            <div className="text-2xl font-bold text-orange-600">{stats.pending_actions.trips_pending_invoice}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Total Invoiced</div>
            <div className="text-2xl font-bold">SAR {stats.financial.total_invoiced?.toLocaleString()}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Pending Supplier Pay</div>
            <div className="text-2xl font-bold text-red-600">{stats.pending_actions.trips_pending_supplier_payment}</div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="jobs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="jobs">Jobs ({jobs.length})</TabsTrigger>
          <TabsTrigger value="trips">All Trips ({trips.length})</TabsTrigger>
          <TabsTrigger value="pending">Pending Actions</TabsTrigger>
        </TabsList>

        {/* Jobs Tab */}
        <TabsContent value="jobs">
          <Card>
            <div className="p-4">
              <h3 className="font-semibold mb-4">Transport Jobs</h3>
              {jobs.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No transport jobs. Create one from a Transportation Client PO.</p>
              ) : (
                <div className="space-y-3">
                  {jobs.map(job => (
                    <div key={job.id} className="border rounded-lg p-4 hover:bg-muted/50" data-testid={`job-${job.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold">{job.job_number}</span>
                            <Badge className={jobStatusConfig[job.status]?.color}>
                              {jobStatusConfig[job.status]?.label}
                            </Badge>
                            <span className="text-sm text-muted-foreground capitalize">{job.job_type}</span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Client: </span>
                              <span className="font-medium">{job.client_name}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              <span>{job.origin} → {job.destination}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Trips: </span>
                              <span className="font-medium">{job.completed_trips}/{job.total_trips}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Value: </span>
                              <span className="font-medium">{job.currency} {job.total_estimated_value?.toLocaleString()}</span>
                            </div>
                          </div>
                          {job.is_outsourced && (
                            <div className="mt-2 text-sm">
                              <Badge variant="outline" className="text-purple-700">
                                Outsourced: {job.supplier_name}
                              </Badge>
                            </div>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewJobDetails(job)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {job.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleStartJob(job.id)}>
                                <Play className="h-4 w-4 mr-2" />
                                Start Job
                              </DropdownMenuItem>
                            )}
                            {['draft', 'scheduled', 'in_progress'].includes(job.status) && (
                              <DropdownMenuItem onClick={() => openAddTrip(job)}>
                                <Plus className="h-4 w-4 mr-2" />
                                Add Trip
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Trips Tab */}
        <TabsContent value="trips">
          <Card>
            <div className="p-4">
              <h3 className="font-semibold mb-4">All Trips</h3>
              {trips.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No trips created yet.</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-muted-foreground">
                      <th className="pb-2">Trip #</th>
                      <th className="pb-2">Route</th>
                      <th className="pb-2">Vehicle</th>
                      <th className="pb-2">Driver</th>
                      <th className="pb-2">Status</th>
                      <th className="pb-2">DN</th>
                      <th className="pb-2">Invoice</th>
                      <th className="pb-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trips.map(trip => (
                      <tr key={trip.id} className="border-b hover:bg-muted/50" data-testid={`trip-${trip.id}`}>
                        <td className="py-3 font-medium">{trip.trip_number}</td>
                        <td className="py-3 text-sm">{trip.origin} → {trip.destination}</td>
                        <td className="py-3">{trip.vehicle_number}</td>
                        <td className="py-3">{trip.driver_name}</td>
                        <td className="py-3">
                          <Badge className={tripStatusConfig[trip.status]?.color}>
                            {tripStatusConfig[trip.status]?.label}
                          </Badge>
                        </td>
                        <td className="py-3">
                          {trip.delivery_note_number ? (
                            <Badge className="bg-green-100 text-green-800">{trip.delivery_note_number}</Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                        <td className="py-3">
                          {trip.invoiced ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <Clock className="h-4 w-4 text-amber-500" />
                          )}
                        </td>
                        <td className="py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {trip.status === 'pending' && (
                                <DropdownMenuItem onClick={() => handleStartTrip(trip.id)}>
                                  <Play className="h-4 w-4 mr-2" />
                                  Start Trip
                                </DropdownMenuItem>
                              )}
                              {['pending', 'in_transit'].includes(trip.status) && (
                                <DropdownMenuItem onClick={() => handleDeliverTrip(trip.id)}>
                                  <Package className="h-4 w-4 mr-2" />
                                  Mark Delivered
                                </DropdownMenuItem>
                              )}
                              {trip.status === 'delivered' && !trip.invoiced && (
                                <DropdownMenuItem onClick={() => handleCreateInvoice(trip.id)}>
                                  <FileText className="h-4 w-4 mr-2" />
                                  Create Invoice
                                </DropdownMenuItem>
                              )}
                              {trip.status === 'delivered' && trip.supplier_cost && !trip.supplier_paid && (
                                <DropdownMenuItem onClick={() => handleSupplierPayment(trip.id)}>
                                  <DollarSign className="h-4 w-4 mr-2" />
                                  Pay Supplier
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Pending Actions Tab */}
        <TabsContent value="pending">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Pending Invoice */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-orange-600" />
                Pending Invoices
              </h3>
              {trips.filter(t => t.status === 'delivered' && !t.invoiced).length === 0 ? (
                <p className="text-muted-foreground text-sm">No trips pending invoice</p>
              ) : (
                <div className="space-y-2">
                  {trips.filter(t => t.status === 'delivered' && !t.invoiced).map(trip => (
                    <div key={trip.id} className="flex items-center justify-between p-2 bg-orange-50 rounded">
                      <div>
                        <span className="font-medium">{trip.trip_number}</span>
                        <span className="text-sm text-muted-foreground ml-2">
                          {trip.currency} {trip.trip_rate?.toLocaleString()}
                        </span>
                      </div>
                      <Button size="sm" onClick={() => handleCreateInvoice(trip.id)}>
                        Invoice
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Pending Supplier Payment */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-red-600" />
                Pending Supplier Payments
              </h3>
              {trips.filter(t => t.status === 'delivered' && t.supplier_cost && !t.supplier_paid).length === 0 ? (
                <p className="text-muted-foreground text-sm">No pending supplier payments</p>
              ) : (
                <div className="space-y-2">
                  {trips.filter(t => t.status === 'delivered' && t.supplier_cost && !t.supplier_paid).map(trip => (
                    <div key={trip.id} className="flex items-center justify-between p-2 bg-red-50 rounded">
                      <div>
                        <span className="font-medium">{trip.trip_number}</span>
                        <span className="text-sm text-muted-foreground ml-2">
                          {trip.currency} {trip.supplier_cost?.toLocaleString()}
                        </span>
                      </div>
                      <Button size="sm" variant="destructive" onClick={() => handleSupplierPayment(trip.id)}>
                        Pay
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Job Dialog */}
      <Dialog open={jobDialogOpen} onOpenChange={setJobDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Transport Job</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateJob} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Operating Company *</Label>
                <Select 
                  value={jobForm.company_id} 
                  onValueChange={(v) => setJobForm({...jobForm, company_id: v, client_po_id: '', supplier_id: ''})}
                  required
                >
                  <SelectTrigger data-testid="job-company-select">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Client PO (Transportation) *</Label>
                <Select 
                  value={jobForm.client_po_id} 
                  onValueChange={(v) => setJobForm({...jobForm, client_po_id: v})}
                  required
                  disabled={!jobForm.company_id}
                >
                  <SelectTrigger data-testid="job-po-select">
                    <SelectValue placeholder="Select PO" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredPOs.map(po => (
                      <SelectItem key={po.id} value={po.id}>
                        {po.po_number} - {po.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {filteredPOs.length === 0 && jobForm.company_id && (
                  <p className="text-xs text-red-500 mt-1">No Transportation POs found for this company</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Job Type</Label>
                <Select value={jobForm.job_type} onValueChange={(v) => setJobForm({...jobForm, job_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single_trip">Single Trip</SelectItem>
                    <SelectItem value="multi_trip">Multi-Trip</SelectItem>
                    <SelectItem value="recurring">Recurring</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cargo Type</Label>
                <Select value={jobForm.cargo_type} onValueChange={(v) => setJobForm({...jobForm, cargo_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General Cargo</SelectItem>
                    <SelectItem value="hazardous">Hazardous</SelectItem>
                    <SelectItem value="oversized">Oversized</SelectItem>
                    <SelectItem value="temperature_controlled">Temperature Controlled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Cargo Description *</Label>
              <Textarea 
                value={jobForm.cargo_description}
                onChange={(e) => setJobForm({...jobForm, cargo_description: e.target.value})}
                required
                placeholder="Describe the cargo..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Origin *</Label>
                <Input 
                  value={jobForm.origin}
                  onChange={(e) => setJobForm({...jobForm, origin: e.target.value})}
                  required
                  placeholder="Pickup location"
                />
              </div>
              <div>
                <Label>Destination *</Label>
                <Input 
                  value={jobForm.destination}
                  onChange={(e) => setJobForm({...jobForm, destination: e.target.value})}
                  required
                  placeholder="Delivery location"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Distance (km)</Label>
                <Input 
                  type="number"
                  value={jobForm.distance_km}
                  onChange={(e) => setJobForm({...jobForm, distance_km: e.target.value})}
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label>Rate Type</Label>
                <Select value={jobForm.rate_type} onValueChange={(v) => setJobForm({...jobForm, rate_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_trip">Per Trip</SelectItem>
                    <SelectItem value="per_km">Per KM</SelectItem>
                    <SelectItem value="flat_rate">Flat Rate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rate Amount (SAR) *</Label>
                <Input 
                  type="number"
                  value={jobForm.rate_amount}
                  onChange={(e) => setJobForm({...jobForm, rate_amount: e.target.value})}
                  required
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vehicle Type</Label>
                <Select value={jobForm.vehicle_type} onValueChange={(v) => setJobForm({...jobForm, vehicle_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="trailer">Trailer</SelectItem>
                    <SelectItem value="tanker">Tanker</SelectItem>
                    <SelectItem value="flatbed">Flatbed</SelectItem>
                    <SelectItem value="refrigerated">Refrigerated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Scheduled Date</Label>
                <Input 
                  type="date"
                  value={jobForm.scheduled_date}
                  onChange={(e) => setJobForm({...jobForm, scheduled_date: e.target.value})}
                />
              </div>
            </div>

            {/* Outsourcing Section */}
            <div className="border rounded-lg p-4 bg-purple-50/50">
              <div className="flex items-center gap-2 mb-3">
                <input 
                  type="checkbox"
                  id="is_outsourced"
                  checked={jobForm.is_outsourced}
                  onChange={(e) => setJobForm({...jobForm, is_outsourced: e.target.checked})}
                />
                <Label htmlFor="is_outsourced" className="font-semibold text-purple-800">
                  Outsource to Supplier
                </Label>
              </div>
              {jobForm.is_outsourced && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Supplier</Label>
                    <Select 
                      value={jobForm.supplier_id} 
                      onValueChange={(v) => setJobForm({...jobForm, supplier_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredSuppliers.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Supplier Cost (SAR)</Label>
                    <Input 
                      type="number"
                      value={jobForm.supplier_cost}
                      onChange={(e) => setJobForm({...jobForm, supplier_cost: e.target.value})}
                      placeholder="Cost to pay supplier"
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea 
                value={jobForm.notes}
                onChange={(e) => setJobForm({...jobForm, notes: e.target.value})}
                placeholder="Additional notes..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setJobDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-job-btn">Create Job</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Trip Dialog */}
      <Dialog open={tripDialogOpen} onOpenChange={setTripDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Trip to {selectedJob?.job_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTrip} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vehicle Number *</Label>
                <Input 
                  value={tripForm.vehicle_number}
                  onChange={(e) => setTripForm({...tripForm, vehicle_number: e.target.value})}
                  required
                  placeholder="e.g., ABC-1234"
                />
              </div>
              <div>
                <Label>Driver Name *</Label>
                <Input 
                  value={tripForm.driver_name}
                  onChange={(e) => setTripForm({...tripForm, driver_name: e.target.value})}
                  required
                  placeholder="Driver name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Departure Date *</Label>
                <Input 
                  type="date"
                  value={tripForm.departure_date}
                  onChange={(e) => setTripForm({...tripForm, departure_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Departure Time</Label>
                <Input 
                  type="time"
                  value={tripForm.departure_time}
                  onChange={(e) => setTripForm({...tripForm, departure_time: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cargo Weight (tons)</Label>
                <Input 
                  type="number"
                  value={tripForm.cargo_weight}
                  onChange={(e) => setTripForm({...tripForm, cargo_weight: e.target.value})}
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label>Trip Rate (SAR)</Label>
                <Input 
                  type="number"
                  value={tripForm.trip_rate}
                  onChange={(e) => setTripForm({...tripForm, trip_rate: e.target.value})}
                  placeholder={`Default: ${selectedJob?.rate_amount}`}
                />
              </div>
            </div>

            {selectedJob?.is_outsourced && (
              <div>
                <Label>Supplier Cost (SAR)</Label>
                <Input 
                  type="number"
                  value={tripForm.supplier_cost}
                  onChange={(e) => setTripForm({...tripForm, supplier_cost: e.target.value})}
                  placeholder={`Default: ${selectedJob?.supplier_cost}`}
                />
              </div>
            )}

            <div>
              <Label>Notes</Label>
              <Textarea 
                value={tripForm.notes}
                onChange={(e) => setTripForm({...tripForm, notes: e.target.value})}
                placeholder="Trip-specific notes..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTripDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-trip-btn">Add Trip</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Job Details Dialog */}
      <Dialog open={viewJobOpen} onOpenChange={setViewJobOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Job Details: {selectedJob?.job_number}</DialogTitle>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div>
                    <Badge className={jobStatusConfig[selectedJob.status]?.color}>
                      {jobStatusConfig[selectedJob.status]?.label}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Client</Label>
                  <p className="font-medium">{selectedJob.client_name}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Route</Label>
                  <p className="font-medium">{selectedJob.origin} → {selectedJob.destination}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Estimated Value</Label>
                  <p className="font-medium">{selectedJob.currency} {selectedJob.total_estimated_value?.toLocaleString()}</p>
                </div>
              </div>

              {selectedJob.is_outsourced && (
                <div className="bg-purple-50 p-3 rounded-lg">
                  <Label className="text-purple-800">Outsourced to</Label>
                  <p className="font-medium">{selectedJob.supplier_name}</p>
                  <p className="text-sm text-muted-foreground">Cost: {selectedJob.currency} {selectedJob.supplier_cost?.toLocaleString()}</p>
                </div>
              )}

              {/* Trips List */}
              <div>
                <h4 className="font-semibold mb-2">Trips ({selectedJob.trips?.length || 0})</h4>
                {selectedJob.trips?.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No trips yet</p>
                ) : (
                  <div className="space-y-2">
                    {selectedJob.trips?.map(trip => (
                      <div key={trip.id} className="border rounded p-3 flex items-center justify-between">
                        <div>
                          <span className="font-medium">{trip.trip_number}</span>
                          <Badge className={`ml-2 ${tripStatusConfig[trip.status]?.color}`}>
                            {tripStatusConfig[trip.status]?.label}
                          </Badge>
                          <div className="text-sm text-muted-foreground mt-1">
                            {trip.vehicle_number} | {trip.driver_name} | {trip.departure_date}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{trip.currency} {trip.trip_rate?.toLocaleString()}</p>
                          {trip.delivery_note_number && (
                            <p className="text-xs text-green-600">DN: {trip.delivery_note_number}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransportationPage;
